import type {
  FulfillmentOrderLine,
  FulfillmentReservation,
  InventoryRow,
} from '@/infrastructure/FulfillmentAdminClient';
import { linePlatformLabel, lineRouteLabel } from '@/utils/fulfillmentSource';

/** Warehouse-held stock is tinted, so a line the order did not have to buy in reads apart. */
export const EXISTING_INVENTORY = 'EXISTING_INVENTORY';

/** A line that records no platform has only its purchase URL to hint where it was bought. */
const jitChannel = (purchaseUrl: string | null | undefined): string | undefined => {
  const url = (purchaseUrl ?? '').toLowerCase();
  if (!url) {
    return undefined;
  }
  if (url.includes('coupang')) {
    return 'Coupang';
  }
  if (url.includes('naver')) {
    return 'Naver';
  }
  return 'Other retail';
};

/**
 * How the wireframe's line table reads a line. The four sourcing routes are printed as plain bold
 * text with no colour, and JIT carries the platform recorded for the line, the URL guess only without one.
 */
export const sourcingRouteLabel = (line: FulfillmentOrderLine): string => {
  const route = line.sourcingRoute ?? '';
  if (!route) {
    return '–';
  }
  // What the order team recorded outranks the URL guess [SSD-7058].
  return lineRouteLabel(route, linePlatformLabel(line) ?? jitChannel(line.purchaseUrl));
};

/** The retired V1 mirror's reserved counter: a number, never a shelf anything can be booked into. */
const V1_RESERVED_LOCATION = 'V1#RESERVED';
/** V1's one-bucket warehouse: real stock, but no shelf anyone can walk to, so a real shelf outranks it. */
const V1_WAREHOUSE_LOCATION = 'WH#00';

/** ACTIVE holds the stock now and CONSUMED is what shipped; a RELEASED reservation no longer says where anything is. */
const LOCATING_STATES = new Set(['ACTIVE', 'CONSUMED']);

/**
 * Where the goods sit. The line never carries a location of its own — the reservation the arrival
 * made is what holds one, which is also the shelf a Cancel Inbound puts the stock back on. Only a live
 * or shipped reservation on a real shelf counts: a released one, or one on a V1 location, would send
 * the line somewhere the server refuses to book, so the line falls back to the order's Shelf instead.
 */
export const lineLocations = (reservations: FulfillmentReservation[]): Map<number, string> =>
  new Map(
    reservations.flatMap((reservation) =>
      LOCATING_STATES.has(reservation.state) &&
      reservation.locationCode &&
      reservation.locationCode !== V1_RESERVED_LOCATION &&
      reservation.locationCode !== V1_WAREHOUSE_LOCATION
        ? [[reservation.lineNo, reservation.locationCode] as [number, string]]
        : [],
    ),
  );

/**
 * What the Location column reads [SSD-7526]: the line's real shelf, or WH#00 when V1's bucket is all it holds, so the
 * cell can say on hover why it shows a dash. Only the screen reads this; where an inbound goes stays `lineLocations`,
 * which leaves WH#00 out because nothing can be booked into it.
 */
export const lineShownLocations = (reservations: FulfillmentReservation[]): Map<number, string> => {
  const shown = lineLocations(reservations);
  for (const reservation of reservations) {
    if (
      LOCATING_STATES.has(reservation.state) &&
      reservation.locationCode === V1_WAREHOUSE_LOCATION &&
      !shown.has(reservation.lineNo)
    ) {
      shown.set(reservation.lineNo, V1_WAREHOUSE_LOCATION);
    }
  }
  return shown;
};

/** WH#00 is real stock but no shelf anyone can walk to, so screens show a dash for it, as labels print a blank. */
export const isV1WarehouseLocation = (locationCode: string | null | undefined): boolean =>
  locationCode === V1_WAREHOUSE_LOCATION;

/**
 * Where each product already sits, as the inbound request's suggested shelf reads it on the server:
 * real shelves before the V1 bucket, then the one holding the most, then by code. One shelf holds one
 * product, so an arriving line goes where its own product already is rather than onto a shared shelf.
 */
export const stockedLocationBySku = (rows: InventoryRow[]): Record<string, string> => {
  const ranked = rows
    .filter((row) => row.onHand > 0 && row.locationCode !== V1_RESERVED_LOCATION)
    .sort(
      (a, b) =>
        a.sku.localeCompare(b.sku) ||
        Number(a.locationCode === V1_WAREHOUSE_LOCATION) - Number(b.locationCode === V1_WAREHOUSE_LOCATION) ||
        b.onHand - a.onHand ||
        a.locationCode.localeCompare(b.locationCode),
    );
  const bySku: Record<string, string> = {};
  for (const row of ranked) {
    bySku[row.sku] ??= row.locationCode;
  }
  return bySku;
};

/** Whether the line is the one whose waybill the desk searched; waybills are compared the way they print. */
export const matchesSearchedTracking = (line: FulfillmentOrderLine, searched: string | null | undefined): boolean => {
  const wanted = (searched ?? '').trim().toUpperCase();
  return wanted !== '' && (line.lineTrackingNumber ?? '').trim().toUpperCase() === wanted;
};

/**
 * The scanned product, and the line whose waybill was searched, are pulled to the top and highlighted
 * [SSD-7053]. The bought lines the desk is receiving come next, and the existing-inventory lines sit together at the
 * bottom, so every shelf pick in a mixed order is read in one place instead of between bought lines (FC request 14,
 * SSD-7568). Each group keeps line order.
 */
export const orderLinesForDisplay = (
  lines: FulfillmentOrderLine[],
  scannedSku: string | undefined,
  searchedTracking?: string | null,
): FulfillmentOrderLine[] => {
  const byLineNo = [...lines].sort((a, b) => a.lineNo - b.lineNo);
  const isHit = (line: FulfillmentOrderLine) =>
    (scannedSku !== undefined && line.sku === scannedSku) || matchesSearchedTracking(line, searchedTracking);
  const rest = byLineNo.filter((line) => !isHit(line));
  const fromShelf = (line: FulfillmentOrderLine) => line.sourcingRoute === EXISTING_INVENTORY;
  return [...byLineNo.filter(isHit), ...rest.filter((line) => !fromShelf(line)), ...rest.filter(fromShelf)];
};
